import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { newId } from '../api.js';
import { useMe } from '../App.jsx';
import { CameraIcon, ErrorBox, ImageIcon, JobPage } from '../components.jsx';
import { isImage, mimeOf, prepare } from '../lib/media.js';
import { enqueue } from '../lib/outbox.js';
import { Thumb } from './AddFiles.jsx';

const COPY = {
  out: { title: 'Sent to job', label: 'What was sent?', placeholder: 'e.g. 6 boxes UT4', done: 'Photos of the goods going out are optional.' },
  in: { title: 'Returned from job', label: 'What came back?', placeholder: 'e.g. 0.5 box UT4', done: 'Photos of the goods coming back are optional.' },
};

function Form({ job, dir }) {
  const { maxUploadMb } = useMe();
  const nav = useNavigate();
  const [sid] = useState(newId);
  const [files, setFiles] = useState([]);
  const [text, setText] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const cam = useRef(null);
  const gallery = useRef(null);
  const copy = COPY[dir];

  function add(list) {
    const bad = [];
    const ok = [];
    for (const f of list) {
      if (!isImage(f)) bad.push(`${f.name} (not a photo)`);
      else if (f.size > maxUploadMb * 1024 * 1024) bad.push(`${f.name} (too big)`);
      else ok.push(f);
    }
    setError(bad.length ? `Couldn't add: ${bad.join(', ')}` : null);
    setFiles((cur) => [...cur, ...ok]);
  }

  async function save() {
    setBusy(true);
    const items = [];
    for (let i = 0; i < files.length; i++) {
      const { blob, mime } = await prepare(files[i]);
      items.push({
        id: newId(), kind: 'file', jobId: job.id, jobName: job.name, sid,
        name: files[i].name || `photo-${i + 1}`, blob, mime: mime || mimeOf(files[i]), query: `as=goods-${dir}`,
      });
    }
    // The note waits (in the queue) until every photo has gone up, then posts once.
    items.push({ id: newId(), kind: 'goods', jobId: job.id, jobName: job.name, sid, direction: dir, text: text.trim() });
    await enqueue(items);
    nav(`/job/${job.id}/done?what=goods&sid=${sid}&n=${files.length}`, { replace: true });
  }

  const input = (ref, props) => (
    <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { add([...e.target.files]); e.target.value = ''; }} {...props} />
  );

  return (
    <>
      {input(cam, { capture: 'environment' })}
      {input(gallery, { multiple: true })}

      <div>
        <label className="label" htmlFor="goods-text">{copy.label}</label>
        <textarea
          id="goods-text" className="input !text-lg" rows={3} maxLength={1000} autoFocus
          placeholder={copy.placeholder} value={text} onChange={(e) => setText(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button type="button" className="btn-ghost flex-col !py-4" onClick={() => cam.current.click()}>
          <CameraIcon className="h-8 w-8" /> Take photo
        </button>
        <button type="button" className="btn-ghost flex-col !py-4" onClick={() => gallery.current.click()}>
          <ImageIcon className="h-8 w-8" /> Choose from phone
        </button>
      </div>
      <p className="-mt-2 text-xs text-steel">{copy.done}</p>

      {error && <ErrorBox>{error}</ErrorBox>}
      {files.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {files.map((f, i) => <Thumb key={`${f.name}-${i}-${f.size}`} file={f} onRemove={() => setFiles(files.filter((_, j) => j !== i))} />)}
        </div>
      )}

      <button className="btn-primary w-full" disabled={busy || text.trim().length < 2} onClick={save}>
        {busy ? 'Getting ready…' : files.length ? `Save with ${files.length} photo${files.length === 1 ? '' : 's'}` : 'Save'}
      </button>
    </>
  );
}

export default function Goods() {
  const { dir } = useParams();
  const { user } = useMe();
  if (user.role !== 'warehouse' || !COPY[dir]) return <ErrorBox>That isn't available.</ErrorBox>;
  return <JobPage title={COPY[dir].title}>{(job) => <Form job={job} dir={dir} />}</JobPage>;
}
